import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Annotation, AnnotationType, AssetType, Course, getDefaultChoicesQuiz, Scene, SceneType, typeData } from '@shared/models';
import { from, Observable } from 'rxjs';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { filter, map, switchMap } from 'rxjs/operators';
import { HotspotDialogComponent } from '@app/main/apps/forms/scenes/hotspotDialog/hotspotDialog.component';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { QuizSettingsComponent } from '@app/main/apps/forms/quiz/quiz-settings/quiz-settings.component';

@Component({
  selector: 'add-annotation-menu',
  templateUrl: './add-annotation-menu.component.html',
  styleUrls: ['./add-annotation-menu.component.scss']
})
export class AddAnnotationMenuComponent implements OnInit {
  @Input() supportedAnnotationTypes: Array<AnnotationType>;
  @Input() scene: Scene;
  @Input() course: Course;
  @Input() presentedAnnotations: Array<Annotation>;
  @Input() isDynamic: boolean;

  @Output() annotationCreated = new EventEmitter<Annotation>();

  fileManager: MatDialogRef<FileManagerComponent>;

  constructor(
    private _dialog: MatDialog,
    private _courseApiService: CoursesAPIService
  ) {
  }

  ngOnInit(): void { }

  isBeta(annotationType: AnnotationType): boolean {
    return annotationType === AnnotationType.npc;
  }

  isDisabled(annotationType: AnnotationType): boolean {
    return annotationType === AnnotationType.npc && this.course.is_multi;
  }

  getIcon(annotationType: AnnotationType): string {
    return typeData.get(annotationType).iconName;
  }

  hasSvgIcon(annotationType: AnnotationType): boolean {
    return !!typeData.get(annotationType).svgIcon;
  }

  getPrimaryAnnotationType(): Array<AnnotationType> {
    return this.supportedAnnotationTypes.filter((annotationType) => annotationType !== AnnotationType.snapZone);
  }

  getSecondaryAnnotationType(): Array<AnnotationType> {
    return this.supportedAnnotationTypes.filter((annotationType) => annotationType === AnnotationType.snapZone);
  }

  canInsertSnapZone(): boolean {
    return this.supportedAnnotationTypes.indexOf(AnnotationType.snapZone) !== -1;
  }

  getTooltip(annotationType: AnnotationType): string {
    if (annotationType === AnnotationType.npc && this.course.is_multi) {
      return 'Switch to Single Player to add a new AI Character';
    }
    return typeData.get(annotationType).displayName;
  }

  // Add an annotation to the current scene
  addAnnotation(annotationType: AnnotationType): void {
    let asset_type;
    switch (annotationType) {
      case AnnotationType.video:
        asset_type = AssetType.v2D;
        break;
      case AnnotationType.image:
        asset_type = AssetType.i2D;
        break;
    }

    let creationObservable: Observable<Annotation>;
    if (asset_type) {
      creationObservable = this.createImageOrVideoAnnotation(asset_type, annotationType);
    } else if (annotationType === AnnotationType.object3D) {
      creationObservable = this.createObject3DAnnotation();
    } else if (annotationType === AnnotationType.hotspot) {
      creationObservable = this.createHotspotAnnotation();
    } else if (annotationType === AnnotationType.text) {
      creationObservable = this.createTextAnnotation();
    } else if (annotationType === AnnotationType.card) {
      creationObservable = this.createCardAnnotation();
    } else if (annotationType === AnnotationType.scorecard) {
      creationObservable = this.createScoreCardAnnotation();
    } else if (annotationType === AnnotationType.quiz) {
      creationObservable = this.createQuizAnnotation();
    } else if (annotationType === AnnotationType.snapZone) {
      creationObservable = this.createSnapZoneAnnotation();
    } else if (annotationType === AnnotationType.npc) {
      creationObservable = this.createNpcAnnotation();
    }

    creationObservable.subscribe(annotation => this.annotationCreated.emit(annotation));
  }

  createObject3DAnnotation(): Observable<Annotation> {
    this.fileManager = this._dialog.open(FileManagerComponent, {
      data: {
        title: 'New Annotation',
        type: AnnotationType.object3D
      },
      id: 'file-manager'
    });
    return this.fileManager.afterClosed().pipe(
      filter(event => event !== undefined),
      switchMap(asset => {
        const properties: any = {
          locked: true
        };

        const newAnnotation = new Annotation(
          AnnotationType.object3D,
          computeAnnotationTitle(this.presentedAnnotations, AnnotationType.object3D, asset.name),
          undefined,
          properties,
          !this.isDynamic ? this.scene.id : undefined,
          asset.id,
          undefined,
          null,
          this.isDynamic ? this.course.id : undefined,
          this.isDynamic
        );

        return this._courseApiService.createAnnotation(newAnnotation).pipe(
          map(annotation => {
            return <Annotation>{ ...annotation, media: asset };
          }));
      })
    );
  }

  createNpcAnnotation(): Observable<Annotation> {
    this.fileManager = this._dialog.open(FileManagerComponent, {
      data: {
        title: 'New Annotation',
        type: AnnotationType.npc,
        openOnTab: 3
      },
      id: 'file-manager'
    });
    return this.fileManager.afterClosed().pipe(
      filter(event => event !== undefined),
      switchMap(asset => from((async () => {
        const properties: any = {
          locked: true
        };

        const newAnnotation = new Annotation(
          AnnotationType.npc,
          computeAnnotationTitle(this.presentedAnnotations, AnnotationType.npc, asset.name),
          undefined,
          properties,
          !this.isDynamic ? this.scene.id : undefined,
          asset.id,
          undefined,
          null,
          this.isDynamic ? this.course.id : undefined,
          this.isDynamic
        );

        const annotation = await this._courseApiService.createAnnotation(newAnnotation).toPromise();

        return <Annotation>{ ...annotation, media: asset };
      })()))
    );
  }

  // Create a new annotation into the database
  createImageOrVideoAnnotation(
    asset_type: AssetType,
    annotation_type: AnnotationType
  ): Observable<Annotation> {
    if (!asset_type) {
      return null;
    }

    this.fileManager = this._dialog.open(FileManagerComponent, {
      data: {
        title: 'New Annotation',
        type: asset_type
      },
      id: 'file-manager'
    });
    return this.fileManager.afterClosed().pipe(
      filter(event => event !== undefined),
      switchMap(asset => {
        const properties: any = {
          custom_duration: false,
          time_in: 0,
          duration: 0
        };

        if (this.scene) {
          properties.zindex = getMaxZindexValue(this.presentedAnnotations) + 1;
        }

        if (this.isDynamic) {
          properties.locked = true;
        }

        const newAnnotation = new Annotation(
          annotation_type,
          computeAnnotationTitle(this.presentedAnnotations, annotation_type, asset.name),
          undefined,
          properties,
          !this.isDynamic ? this.scene.id : undefined,
          asset.id,
          undefined,
          asset_type,
          this.isDynamic ? this.course.id : undefined,
          this.isDynamic
        );

        return this._courseApiService.createAnnotation(newAnnotation).pipe(
          map(annotation => {
            return <Annotation>{ ...annotation, media: asset };
          }));
      })
    );
  }

  createHotspotAnnotation(): Observable<Annotation> {
    return this._dialog
      .open(HotspotDialogComponent, {
        width: '50vw',
        height: '60vh'
      })
      .afterClosed()
      .pipe(
        filter(event => event !== undefined),
        switchMap(hotspot => {
          const properties: any = {
            src: hotspot.path,
            style: { color: '#ffffff' },
            custom_duration: false,
            time_in: 0,
            duration: 0
          };

          if (this.scene) {
            properties.zindex = getMaxZindexValue(this.presentedAnnotations) + 1;
          }

          if (this.isDynamic) {
            properties.locked = true;
          }

          const newAnnotation = new Annotation(
            AnnotationType.hotspot,
            computeAnnotationTitle(this.presentedAnnotations, AnnotationType.hotspot, hotspot.name),
            undefined,
            properties,
            !this.isDynamic ? this.scene.id : undefined,
            null,
            undefined,
            null,
            this.isDynamic ? this.course.id : undefined,
            this.isDynamic
          );
          return this._courseApiService.createAnnotation(newAnnotation);
        })
      );
  }

  createTextAnnotation(): Observable<Annotation> {
    const is360 = this.scene && (this.scene.type === SceneType.i360 || this.scene.type === SceneType.v360);
    const properties: any = {
      textvalue: 'Click here to edit text',
      style: { color: 'white', text_size: (is360) ? 14 : 10 },
      custom_duration: false,
      time_in: 0,
      duration: 0
    };

    if (this.scene) {
      properties.zindex = getMaxZindexValue(this.presentedAnnotations) + 1;
    }

    if (this.isDynamic) {
      properties.locked = true;
    }

    const newAnnotation = new Annotation(
      AnnotationType.text,
      computeAnnotationTitle(this.presentedAnnotations, AnnotationType.text, 'Text'),
      undefined,
      properties,
      !this.isDynamic ? this.scene.id : undefined,
      null,
      undefined,
      null,
      this.isDynamic ? this.course.id : undefined,
      this.isDynamic
    );

    return this._courseApiService.createAnnotation(newAnnotation);
  }

  createCardAnnotation(): Observable<Annotation> {
    const properties: any = {
      body: 'This is a card content',
      custom_duration: false,
      time_in: 0,
      duration: 0
    };

    if (this.scene) {
      properties.zindex = getMaxZindexValue(this.presentedAnnotations) + 1;
    }

    if (this.isDynamic) {
      properties.locked = true;
    }

    const newAnnotation = new Annotation(
      AnnotationType.card,
      computeAnnotationTitle(this.presentedAnnotations, AnnotationType.card, 'Card'),
      undefined,
      properties,
      !this.isDynamic ? this.scene.id : undefined,
      null,
      undefined,
      null,
      this.isDynamic ? this.course.id : undefined,
      this.isDynamic
    );

    return this._courseApiService.createAnnotation(newAnnotation);
  }

  createScoreCardAnnotation(): Observable<Annotation> {
    const properties: any = {
      title: 'Track your progress',
      body: 'Your current score',
      custom_duration: false,
      time_in: 0,
      duration: 0
    };

    if (this.scene) {
      properties.zindex = getMaxZindexValue(this.presentedAnnotations) + 1;
    }
    if (this.isDynamic) {
      properties.locked = true;
    }

    const newAnnotation = new Annotation(
      AnnotationType.scorecard,
      computeAnnotationTitle(this.presentedAnnotations, AnnotationType.scorecard, 'Score Card'),
      undefined,
      properties,
      !this.isDynamic ? this.scene.id : undefined,
      null,
      undefined,
      null,
      this.isDynamic ? this.course.id : undefined,
      this.isDynamic
    );

    return this._courseApiService.createAnnotation(newAnnotation);
  }

  createQuizAnnotation(): Observable<Annotation> {
    const properties: any = {
      quiz: getDefaultChoicesQuiz()
    };

    if (this.scene) {
      properties.zindex = getMaxZindexValue(this.presentedAnnotations) + 1;
    }
    if (this.isDynamic) {
      properties.locked = true;
    }
    const quizAnnotation = new Annotation(
      AnnotationType.quiz,
      computeAnnotationTitle(this.presentedAnnotations, AnnotationType.quiz, 'Quiz'),
      undefined,
      properties,
      !this.isDynamic ? this.scene.id : undefined,
      null,
      undefined,
      null,
      this.isDynamic ? this.course.id : undefined,
      this.isDynamic
    );

    return this._dialog
      .open(QuizSettingsComponent, {
        width: '60vw',
        height: '90vh',
        data: { annotation: quizAnnotation, course: this.course, scene: this.scene }
      })
      .afterClosed()
      .pipe(
        filter(event => event !== undefined),
        switchMap(quizSettings => {
          quizAnnotation.properties.quiz = quizSettings;
          return this._courseApiService.createAnnotation(quizAnnotation);
        })
      );
  }

  createSnapZoneAnnotation(): Observable<Annotation> {
    const scaleFactor = 2;
    const properties: any = {
      locked: true,
      size: {
        type: 'size-2d',
        width: 1 * scaleFactor,
        height: 9 * scaleFactor / 16.0,
        depth: 0.1
      }
    };

    const newAnnotation = new Annotation(
      AnnotationType.snapZone,
      computeAnnotationTitle(this.presentedAnnotations, AnnotationType.snapZone, 'Snap Zone'),
      undefined,
      properties,
      !this.isDynamic ? this.scene.id : undefined,
      null,
      undefined,
      null,
      this.isDynamic ? this.course.id : undefined,
      this.isDynamic
    );


    return this._courseApiService.createAnnotation(newAnnotation);
  }
}

function computeAnnotationTitle(annotations: Array<Annotation>, annotationType: AnnotationType, title: string): string {
  const nb = annotations.filter(a => a.type === annotationType && a.label.startsWith(title)).length;
  if (nb) {
    return `${title} ${nb}`;
  }
  return title;
}

function getMaxZindexValue(annotations: Array<Annotation>): number {
  let max = 0;
  for (const annotation of annotations) {
    if (annotation.properties && typeof annotation.properties.zindex === 'number') {
      max = annotation.properties.zindex > max ? annotation.properties.zindex : max;
    }
  }
  return max;
}
